import React, { useState, useEffect, useMemo } from "react";
import { 
    ReloadOutlined, 
    DollarOutlined, 
    UserOutlined, 
    InfoCircleOutlined, 
    MailOutlined, 
    PhoneOutlined, 
    CalendarOutlined, 
    WalletOutlined,
    ClockCircleOutlined,
    FileTextOutlined
} from "@ant-design/icons";
import { Tag, Modal, Button, Col, Space, Select, Typography, Avatar, Descriptions } from "antd";
import Breadcrumbs from "../../component/Breadcrumbs";
import DeliveryStaffFeeServ from "../../service/DeliveryStaffFeeServ";
import TableActions from "../../component/ui/TableActions";
import { FilterBar } from "../../component/ui/FilterBar";
import SearchInput from "../../component/ui/SearchInput";
import { ResetButton } from "../../component/ui/ActionButtons";
import DataTable from "../../component/ui/DataTable";
import DateRangePicker from "../../component/ui/DateRangePicker";
import QuickFilter from "../../component/ui/QuickFilter";
import dayjs from "dayjs";

const { Text, Title } = Typography;

const { Option } = Select;

const DeliveryStaffFee = () => {
    const [fees, setFees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [dateRange, setDateRange] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [quickFilter, setQuickFilter] = useState(undefined);

    const fetchFees = async () => {
        try {
            setLoading(true);
            const res = await DeliveryStaffFeeServ.index();
            setFees(res || []);
        } catch (error) {
            console.error("Error fetching fees:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFees();
    }, []);

    const columns = [
        { 
            title: "Staff Member", 
            key: "staff", 
            render: (_, record) => (
                <div 
                    style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                >
                    <Avatar 
                        icon={<UserOutlined />} 
                        style={{ backgroundColor: '#f0f2f5', color: '#4776E6' }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 700, color: '#4776E6', fontSize: '13px', textDecoration: 'underline' }}>
                            {record.staffId ? `${record.staffId.firstName} ${record.staffId.lastName}` : "Unknown"}
                        </span>
                        <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                            {record.staffId?.phone || 'No Phone'}
                        </span>
                    </div>
                </div>
            ) 
        },
        { 
            title: "Delivery Ref", 
            key: "delivery", 
            render: (_, record) => (
                <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
                    {record.deliveryId?.id || "Manual"}
                </span>
            )
        },
        { 
            title: "Fee Amount", 
            dataIndex: "amount", 
            key: "amount", 
            align: "right",
            render: (val) => (
                <span style={{ color: '#1e293b', fontWeight: 800, fontSize: '14px' }}>
                    ₹{val}
                </span>
            ) 
        },
        { 
            title: "Status", 
            dataIndex: "status", 
            key: "status", 
            width: 100,
            align: 'center',
            render: (status) => {
                const color = status === 'paid' ? '#22c55e' : '#f59e0b';
                const bg = status === 'paid' ? '#f0fdf4' : '#fffbeb';
                const border = status === 'paid' ? '#dcfce7' : '#fef3c7';
                return (
                    <span style={{
                        padding: '4px 12px',
                        borderRadius: '8px',
                        fontSize: '11px',
                        fontWeight: 700,
                        backgroundColor: bg,
                        color: color,
                        textTransform: 'uppercase',
                        border: `1px solid ${border}`,
                        display: 'inline-block',
                        minWidth: '70px',
                        textAlign: 'center'
                    }}>
                        {status || "PENDING"}
                    </span>
                );
            }
        },
        { 
            title: "Date", 
            dataIndex: "createdAt", 
            key: "date", 
            width: 120,
            render: (val) => (
                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                    {val ? dayjs(val).format('DD MMM YYYY') : "—"}
                </span>
            ) 
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            width: 100,
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(record);
                    }}
                    style={{
                        borderRadius: 3,
                        fontWeight: 600,
                        background: '#ffffffff',
                        color: '#004754ff',
                        border: '1px solid #e4e4e7',
                        boxShadow: '0 0 0 1px #e4e4e7',
                        height: 'auto',
                        padding: '0px 8px',
                        fontSize: 13,
                    }}
                >
                    View
                </Button>
            ),
        },
    ];

    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleRowClick = (record) => {
        setSelectedRecord(record);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedRecord(null);
    };

    const filteredData = fees.filter(item => {
        const staffName = `${item.staffId?.firstName || ''} ${item.staffId?.lastName || ''}`.toLowerCase();
        const refId = (item.deliveryId?.id || 'manual').toLowerCase();
        const matchesSearch = staffName.includes(searchText.toLowerCase()) || refId.includes(searchText.toLowerCase());

        let matchesDate = true;
        if (dateRange && dateRange[0] && dateRange[1]) {
            const itemDate = dayjs(item.createdAt);
            const startDate = dateRange[0].startOf('day');
            const endDate = dateRange[1].endOf('day');
            matchesDate = (itemDate.isAfter(startDate) || itemDate.isSame(startDate)) && 
                          (itemDate.isBefore(endDate) || itemDate.isSame(endDate));
        }

        return matchesSearch && matchesDate;
    });

    const fetchAllFees = async () => {
        return filteredData;
    };

    return (
        <div className="pcu-padding-24">
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs />
            </div>

            <div className="sd-container" style={{ padding: '24px' }}>
                <div className="sd-header" style={{ marginBottom: '24px', background: 'transparent', padding: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div className="sd-orders-icon-wrap" style={{ width: '48px', height: '48px', background: '#fff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                            <DollarOutlined style={{ fontSize: '22px', color: '#4776E6' }} />
                        </div>
                        <div>
                            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' }}>Delivery Staff Fee</h1>
                            <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af', fontWeight: 500 }}>{filteredData.length} records in system</p>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <TableActions
                            onFetchFullData={fetchAllFees}
                            columns={columns}
                            fileName="delivery_staff_fees"
                            pageName="Delivery Staff Fees"
                        />
                        <ResetButton
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                setSearchText("");
                                setDateRange(null);
                                setQuickFilter(undefined);
                                fetchFees();
                            }}
                            text="Refresh"
                        />
                    </div>
                </div>

                <div className="pcu-padding-0">
                    <FilterBar>
                        <Col xs={24} md={8}>
                            <SearchInput
                                label="Search Fees"
                                placeholder="Search by staff name or ref ID..."
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <QuickFilter
                                value={quickFilter}
                                onChange={(key, range) => {
                                    setQuickFilter(key);
                                    setDateRange(range);
                                }}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <DateRangePicker
                                label="Custom Date Range"
                                value={dateRange}
                                onChange={(val) => {
                                    setDateRange(val);
                                    setQuickFilter(undefined);
                                }}
                            />
                        </Col>
                    </FilterBar>

                    <div className="pcu-border-top">
                        <DataTable
                            columns={columns}
                            data={filteredData}
                            rowKey="_id"
                            loading={loading}
                            onRow={(record) => ({
                                style: { cursor: 'default' }
                            })}
                            pagination={{
                                current: page,
                                pageSize: pageSize,
                                total: filteredData.length,
                                showTotal: (total) => `Total ${total} items`,
                                onChange: (p) => setPage(p),
                                onShowSizeChange: (_, size) => setPageSize(size)
                            }}
                        />
                    </div>
                </div>
            </div>

            <Modal
                open={isModalOpen}
                onCancel={handleCloseModal}
                footer={null}
                width={600}
                closable={true}
                className="ss-modal"
                centered
            >
                {selectedRecord && (
                    <div className="ss-modal-body">
                        <div className="ss-modal-hero" style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', gap: 16 }}>
                            <Avatar 
                                size={64} 
                                icon={<UserOutlined />} 
                                src={selectedRecord.staffId?.imageUrl}
                                style={{ 
                                    backgroundColor: '#fff', 
                                    color: '#4776E6',
                                    border: '2px solid #e0e7ff',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                                }}
                            />
                            <div className="ss-modal-info" style={{ gap: 2 }}>
                                <Title level={4} style={{ margin: 0, fontWeight: 800, letterSpacing: '-0.5px' }}>
                                    {selectedRecord.staffId?.firstName} {selectedRecord.staffId?.lastName}
                                </Title>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{
                                        padding: '1px 6px',
                                        borderRadius: '4px',
                                        fontSize: '9px',
                                        fontWeight: 700,
                                        backgroundColor: '#f0fdf4',
                                        color: '#16a34a',
                                        textTransform: 'uppercase',
                                        border: '1px solid #dcfce7'
                                    }}>
                                        DELIVERY STAFF
                                    </span>
                                    <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>
                                        ID: {selectedRecord.staffId?.userId || selectedRecord._id?.substring(0, 12).toUpperCase()}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div style={{ padding: '20px' }}>
                            <div style={{ marginBottom: 15, fontSize: '11px', fontWeight: 800, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px', borderLeft: '3px solid #4776E6', paddingLeft: 8 }}>
                                Staff Contact
                            </div>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: '12px',
                                padding: '12px',
                                background: '#f8fafc',
                                borderRadius: 12,
                                border: '1px solid #f1f5f9',
                                marginBottom: 20
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{ width: 28, height: 28, borderRadius: 6, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                                        <MailOutlined style={{ color: '#4776E6', fontSize: 12 }} />
                                    </div>
                                    <div style={{ overflow: 'hidden' }}>
                                        <div style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Email</div>
                                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#334155', textOverflow: 'ellipsis', overflow: 'hidden' }}>{selectedRecord.staffId?.email || 'N/A'}</div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{ width: 28, height: 28, borderRadius: 6, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                                        <PhoneOutlined style={{ color: '#4776E6', fontSize: 12 }} />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Phone</div>
                                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>{selectedRecord.staffId?.phone || 'N/A'}</div>
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginBottom: 15, fontSize: '11px', fontWeight: 800, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px', borderLeft: '3px solid #10b981', paddingLeft: 8 }}>
                                Transaction Details
                            </div>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: '12px',
                                padding: '16px',
                                background: '#fff',
                                borderRadius: 12,
                                border: '1.5px solid #f1f5f9',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                            }}>
                                <div>
                                    <div style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', marginBottom: 4 }}>Fee Amount</div>
                                    <div style={{ fontSize: '18px', fontWeight: 800, color: '#1e293b' }}>₹{selectedRecord.amount}</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', marginBottom: 4 }}>Status</div>
                                    <span style={{
                                        padding: '4px 12px',
                                        borderRadius: '8px',
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        backgroundColor: selectedRecord.status === 'paid' ? '#f0fdf4' : '#fffbeb',
                                        color: selectedRecord.status === 'paid' ? '#22c55e' : '#f59e0b',
                                        textTransform: 'uppercase',
                                        border: `1px solid ${selectedRecord.status === 'paid' ? '#dcfce7' : '#fef3c7'}`
                                    }}>
                                        {selectedRecord.status}
                                    </span>
                                </div>
                                <div style={{ marginTop: 8 }}>
                                    <div style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', marginBottom: 4 }}>Reference ID</div>
                                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b' }}>{selectedRecord.deliveryId?.id || 'Manual Entry'}</div>
                                </div>
                                <div style={{ textAlign: 'right', marginTop: 8 }}>
                                    <div style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', marginBottom: 4 }}>Created At</div>
                                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b' }}>{dayjs(selectedRecord.createdAt).format('DD MMM YYYY, hh:mm A')}</div>
                                </div>
                                {selectedRecord.notes && (
                                    <div style={{ gridColumn: 'span 2', marginTop: 8, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                                        <div style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', marginBottom: 4 }}>Notes</div>
                                        <div style={{ fontSize: '12px', color: '#475569', lineHeight: 1.5 }}>{selectedRecord.notes}</div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="ss-modal-footer" style={{ padding: '12px 20px', background: '#f8fafc', display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                onClick={handleCloseModal}
                                style={{ borderRadius: 8, fontWeight: 700, height: 34, padding: '0 20px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                            >
                                Close
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default DeliveryStaffFee;
